import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authApi, tokenManager } from '../../services/api';
import { LayoutDashboard, FileText, Files, Image, Settings, Users, FolderOpen, StickyNote, Gift, ShoppingCart, ClipboardList, LogOut, Globe, Boxes, UserCheck, ClipboardCheck, Briefcase, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import logoImg from '../../logo.png';

export default function AdminLayout({ children, active }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const location = useLocation();
  const isPosPage = location.pathname === '/admin/pos';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved !== null ? saved === 'true' : isPosPage;
  });
  const navRef = useRef(null);

  // Auto-collapse on POS page, restore otherwise
  useEffect(() => {
    if (isPosPage) setCollapsed(true);
  }, [isPosPage]);

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', collapsed);
  }, [collapsed]);

  useEffect(() => {
    if (navRef.current) {
      const saved = sessionStorage.getItem('sidebar_scroll');
      if (saved) navRef.current.scrollTop = parseInt(saved, 10);
      const el = navRef.current;
      const onScroll = () => sessionStorage.setItem('sidebar_scroll', el.scrollTop);
      el.addEventListener('scroll', onScroll);
      return () => el.removeEventListener('scroll', onScroll);
    }
  }, []);

  useEffect(() => {
    authApi.me().then(res => setUser(res.data.data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await authApi.logout().catch(() => {});
    tokenManager.clear();
    navigate('/admin/login');
  };

  const links = [
    { to: '/admin/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    { to: '/admin/pos', label: 'POS (Kasir)', Icon: ShoppingCart, highlight: true },
    { type: 'divider', label: 'POS' },
    { to: '/admin/categories', label: 'Kategori', Icon: FolderOpen },
    { to: '/admin/layanan', label: 'Layanan', Icon: StickyNote },
    { to: '/admin/packages', label: 'Paket', Icon: Gift },
    { to: '/admin/orders', label: 'Pesanan', Icon: ClipboardList },
    { type: 'divider', label: 'Operation' },
    { to: '/admin/operation-workers', label: 'Pekerja Operasi', Icon: UserCheck },
    { to: '/admin/operation-inventory', label: 'Inventaris', Icon: Boxes },
    { to: '/admin/operation-categories', label: 'Kategori Inventaris', Icon: FolderOpen },
    { to: '/admin/operation-assignments', label: 'Assignment', Icon: ClipboardCheck },
    { to: '/admin/operation-positions', label: 'Posisi', Icon: Briefcase },
    { to: '/admin/operation-departments', label: 'Departemen', Icon: Building2 },
    { type: 'divider', label: 'CMS' },
    { to: '/admin/pages', label: 'Halaman', Icon: FileText },
    { to: '/admin/sections', label: 'Seksi', Icon: Files },
    { to: '/admin/posts', label: 'Blog', Icon: StickyNote },
    { to: '/admin/media', label: 'Media', Icon: Image },
    { to: '/admin/settings', label: 'Pengaturan', Icon: Settings },
    { to: '/admin/users', label: 'Pengguna', Icon: Users },
  ];

  const handleToggleCollapse = () => {
    if (navRef.current) {
      sessionStorage.setItem('sidebar_scroll', navRef.current.scrollTop);
    }
    setCollapsed(!collapsed);
    requestAnimationFrame(() => {
      if (navRef.current) {
        const saved = sessionStorage.getItem('sidebar_scroll');
        if (saved) navRef.current.scrollTop = parseInt(saved, 10);
      }
    });
  };

  const SidebarContent = () => (
    <>
      <div className={`flex items-center border-b border-gray-100 flex-shrink-0 ${collapsed ? 'justify-center px-2 py-3' : 'justify-between px-4 py-3'}`}>
        {!collapsed && <img src={logoImg} alt="Logo" className="h-8 w-auto" />}
        <button onClick={handleToggleCollapse} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0" title={collapsed ? 'Buka sidebar' : 'Tutup sidebar'}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
      <nav ref={navRef} className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
        {links.map((link, i) => 
          link.type === 'divider' ? (
            !collapsed ? (
              <div key={i} className="pt-2 pb-0.5">
                <span className="px-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{link.label}</span>
              </div>
            ) : <div key={i} className="border-t border-gray-100 my-1" />
          ) : (
            <Link key={link.to} to={link.to} 
              className={`flex items-center rounded-lg text-xs font-medium transition-colors ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5 gap-2.5'} ${active === link.label ? 'bg-brand/10 text-brand-dark' : link.highlight ? 'bg-brand/5 text-brand-dark hover:bg-brand/10' : 'text-gray-600 hover:bg-gray-50'}`} 
              onClick={() => { setSidebarOpen(false); if (isPosPage && link.label === 'POS (Kasir)') setCollapsed(true); }}
              title={collapsed ? link.label : undefined}
            >
              <link.Icon size={collapsed ? 18 : 15} /> {!collapsed && link.label}
            </Link>
          )
        )}
      </nav>
      {!collapsed && (
        <div className="flex-shrink-0 border-t border-gray-100 bg-white px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-brand/10 rounded-full flex items-center justify-center text-brand-dark font-bold text-[10px] flex-shrink-0">{user?.name?.charAt(0)}</div>
            <span className="text-[11px] font-medium text-gray-700 truncate flex-1">{user?.name}</span>
            <button onClick={handleLogout} className="text-red-400 hover:text-red-600 flex-shrink-0" title="Keluar"><LogOut size={14} /></button>
            <Link to="/" target="_blank" className="text-gray-400 hover:text-brand-dark flex-shrink-0" title="Lihat Website"><Globe size={14} /></Link>
          </div>
        </div>
      )}
      {collapsed && (
        <div className="flex-shrink-0 border-t border-gray-100 bg-white flex flex-col items-center gap-2 py-2">
          <button onClick={handleLogout} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600" title="Keluar"><LogOut size={14} /></button>
          <Link to="/" target="_blank" className="p-1.5 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-brand-dark" title="Lihat Website"><Globe size={14} /></Link>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className={`hidden lg:flex flex-col bg-white border-r border-gray-200 sticky top-0 h-screen overflow-hidden transition-all duration-200 ${collapsed ? 'w-14' : 'w-52'}`}>
        <SidebarContent />
      </aside>
      {sidebarOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setSidebarOpen(false)} />
          <aside className={`lg:hidden flex flex-col fixed inset-y-0 left-0 z-40 bg-white border-r border-gray-200 transition-all duration-200 ${collapsed ? 'w-14' : 'w-52'}`}>
            <SidebarContent />
          </aside>
        </>
      )}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between lg:hidden flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="p-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div className="w-6" />
        </header>
        <main className="flex-1 px-6 pt-3 pb-6 overflow-y-auto h-screen">{children}</main>
      </div>
      <div className="fixed bottom-2 right-4 text-[10px] text-black select-none pointer-events-none z-50 font-medium">Made with ❤️ Saskia</div>
    </div>
  );
}